import { useTranslation } from 'react-i18next';
import ModelDialog from './ModelDialog';
import { Box, DialogContent, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Btn from './Btn';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../context/StoreProvider';
import { countryContextFromStore, toTelHrefForContact } from '../../utils/contactPhoneLinks';
import { useRegisterCountries } from '../../hooks/useRegisterCountries';

const ModelIntroductionScreen = ({ openDialog, setOpenDialog, data }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { storeData } = useStore() || {};
    const { countries } = useRegisterCountries();
    const countryContext = countryContextFromStore(storeData, countries);

    // ✅ فحص إذا كان المحتوى رقم هاتف
    const isPhoneNumber = useMemo(() => {
        if (!data?.content) return false;

        // فحص content_type أولاً
        if (data?.content_type === 'phone') return true;

        // فحص إذا كان المحتوى رقم (أرقام فقط أو مع + و -)
        const phoneRegex = /^[\d\s\-\\+\\(\\)]+$/;
        return phoneRegex.test(data.content.trim());
    }, [data]);

    // تحقق إذا كان الرابط بريدًا إلكترونيًا
    const isValidEmail = (url) => {
        if (!url) return false;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(url);
    };

    // ✅ تحديد نوع الرابط بدقة (مع استبعاد الأرقام)
    const isExternal = useMemo(() => {
        if (!data?.content) return false;

        // ✅ استبعاد الأرقام من التحقق
        if (isPhoneNumber) return false;

        if (isValidEmail(data.content)) return true;

        try {
            new URL(data.content);
            return true;
        } catch {
            return (
                data.content?.includes('.') &&
                !data.content?.startsWith('/') &&
                !data.content?.startsWith('#') &&
                !data.content?.startsWith('mailto:') &&
                !data.content?.startsWith('tel:')
            );
        }
    }, [data?.content, isPhoneNumber]);

    // ✅ معالجة الروابط بترتيب صحيح
    const normalizedUrl = useMemo(() => {
        if (!data?.content) return '';

        // 1️⃣ أولوية للهاتف
        if (isPhoneNumber) {
            return toTelHrefForContact(data.content, countryContext);
        }

        // 2️⃣ ثم الإيميل
        if (isValidEmail(data.content)) {
            return `mailto:${data.content}`;
        }

        // 3️⃣ ثم الروابط الداخلية
        if (!isExternal) {
            return data.content;
        }

        // 4️⃣ أخيراً الروابط الخارجية
        return data.content.startsWith('http')
            ? data.content
            : `https://${data.content}`;
    }, [data, isPhoneNumber, isExternal, countryContext]);


    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    // ✅ معالج النقر الموحد للزر
    const handleAgreeClick = (e) => {
        e.preventDefault();

        try {
            // ✅ للهواتف والإيميلات والروابط الخارجية
            if (isPhoneNumber || isValidEmail(data?.content) || isExternal) {
                window.location.href = normalizedUrl;
            } else {
                // للروابط الداخلية
                navigate(normalizedUrl);
            }

            // إغلاق الـ Dialog بعد التنقل
            handleCloseDialog();
        } catch (error) {
            console.error('[ModelIntroductionScreen] Navigation error:', error);
            // في حالة فشل التنقل، حاول فتح الرابط في نافذة جديدة
            window.open(normalizedUrl, '_blank');
        }
    };

    return (
        <>
            <ModelDialog
                openDialog={openDialog}
                setOpenDialog={setOpenDialog}
            >
                <DialogContent sx={{
                    background: `url(${data?.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    width: '100%',
                    p: 1,
                    height: '60dvh',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        py: 1,
                        px: 2,
                    }}>
                        <IconButton onClick={handleCloseDialog} sx={{ color: '#fff' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box>
                        <Box sx={{
                            padding: '8px',
                            background: '#0000008c',
                            margin: '8px',
                            borderRadius: '8px'
                        }}>
                            <Typography sx={{ color: '#fff', margin: '8px 0' }}>
                                {data?.description}
                            </Typography>
                        </Box>

                        <Box display="flex" gap={2} mb={2} sx={{
                            justifyContent: 'center',
                            color: theme => theme.palette.primary.main,
                            '& > *': {
                                flex: 1,
                                maxWidth: { xs: '150px', sm: '200px' },
                                minWidth: '120px'
                            }
                        }}>
                            {/* زر المتابعة */}
                            <Btn
                                fullWidth
                                onClick={handleAgreeClick}
                                sx={{
                                    color: theme => theme.palette.primary.main,
                                    background: theme => theme.palette.primary.text,
                                    mx: 0,
                                    px: 2,
                                    minWidth: 'unset',
                                    fontSize: '0.875rem',
                                    cursor: 'pointer',
                                    touchAction: 'manipulation',
                                    '& .MuiTypography-root': {
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        pointerEvents: 'none'
                                    }
                                }}
                                br
                            >
                                <Typography color={'primary.main'} variant='body1'>
                                    {t('IntroductionScreen.agree')}
                                </Typography>
                            </Btn>

                            {/* زر التخطي */}
                            <Btn
                                fullWidth
                                onClick={handleCloseDialog}
                                sx={{
                                    color: theme => theme.palette.primary.main,
                                    background: theme => theme.palette.primary.text,
                                    mx: 0,
                                    px: 2,
                                    minWidth: 'unset',
                                    fontSize: '0.875rem',
                                    cursor: 'pointer',
                                    touchAction: 'manipulation',
                                    '& .MuiTypography-root': {
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        pointerEvents: 'none'
                                    }
                                }}
                                br
                            >
                                <Typography color={'primary.main'} variant='body1'>
                                    {t('IntroductionScreen.skip')}
                                </Typography>
                            </Btn>
                        </Box>
                    </Box>
                </DialogContent>
            </ModelDialog>
        </>
    );
};

export default ModelIntroductionScreen;
